// Per-user, per-boat alarm NOTIFICATION preferences (Phase UI-5) — the read/write
// hook behind the Alarms page's Notifications card. Backs the P8 REST contract
// (§5.9, ADR 0153 + ADR 0171):
//
//   GET  /v1/boats/:boatId/notification-prefs
//   PUT  /v1/boats/:boatId/notification-prefs
//     → { emailEnabled, minSeverity, recoveryEmailEnabled, offlineAlertEnabled,
//         offlineThresholdMinutes, deliveryEnabled, rateLimit }
//
// `deliveryEnabled` and `rateLimit` are READ-ONLY, server-derived — the first
// from the production gate AND the NEOMARIS_CLOUD_ALERT_EMAILS kill switch, the second
// from the ADR 0153 trailing-hour throttle's own delivery ledger — so the card
// can render dry-run honesty and stop the throttle suppressing invisibly.
// Neither is ever sent on a PUT. Prefs are strictly self-scoped (each user their
// own row) and an ABSENT row is the defaults (email on, `warning` floor,
// recovery mail ON, gone-quiet alert OFF at a 30-minute threshold).
//
// A plain GET on mount + a PUT on change — NO WebSocket lane (prefs are a
// personal setting, not live boat telemetry). NO optimistic UI: the card always
// reflects the value the PUT response returns, so a rejected write can never
// leave a checkbox lying about what the server stored. The pure helpers
// (parse / coerce / label / error-message) are exported so they unit-test
// without a DOM — the same convention as `useBoatAlerts`.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { ALARM_SEVERITIES, type AlarmSeverity } from './alarmDisplay';

/**
 * Gone-quiet threshold bounds. These MIRROR the server's own constants
 * (`cloud/src/core/alerts/boat-quiet.ts`, and the DB CHECK on
 * `user_notification_prefs.offline_threshold_minutes`) — they are duplicated
 * because those live in server-only code, not a shared package. The server is
 * authoritative: it clamps on read and rejects out-of-range writes with a 400.
 * We mirror them only so the card never OFFERS a value the server would reject.
 */
export const OFFLINE_THRESHOLD_MIN_MINUTES = 15;
export const OFFLINE_THRESHOLD_MAX_MINUTES = 10080;
export const OFFLINE_THRESHOLD_DEFAULT_MINUTES = 30;

/** The three thresholds the card offers. A stored value outside this set (set
 *  through the API directly) is still shown — see {@link offlineThresholdOptions}. */
export const OFFLINE_THRESHOLD_CHOICES: readonly number[] = [15, 30, 60];

/** One alarm of this boat whose emails the throttle is currently suppressing. */
export interface RateLimitedAlert {
  readonly alertId: string;
  /** Emails actually sent for this alarm inside the trailing window. */
  readonly sentInWindow: number;
  /** Oldest still-counted send. */
  readonly since: string;
  /** Exact instant the count falls back below the cap. */
  readonly resumesAt: string;
}

/**
 * The read-only throttle block. **Boat-scoped, not user-scoped**: the ADR 0153
 * cap keys on `(boat, alarm)` and is SHARED by every owner of the boat
 * (`scope: 'boat_alert'`). The card must not render it as a personal quota —
 * see `specs/api_contracts.md` §5.9.
 */
export interface RateLimitState {
  readonly limited: boolean;
  /** What the cap is keyed on, verbatim from the server. */
  readonly scope: string;
  /** Max sends per key per window. */
  readonly max: number;
  readonly windowMinutes: number;
  readonly since: string | null;
  /** When the boat is fully un-suppressed again; null when nothing is limited. */
  readonly resumesAt: string | null;
  readonly alerts: readonly RateLimitedAlert[];
}

/** The prefs the card renders: the stored fields + the two derived blocks. */
export interface NotificationPrefs {
  /** Whether this user wants alarm emails for this boat. */
  readonly emailEnabled: boolean;
  /** The inclusive severity floor — `warning` admits warning/alarm/emergency. */
  readonly minSeverity: AlarmSeverity;
  /** The "back to normal" mail when an alarm clears. Opt-OUT: default ON. */
  readonly recoveryEmailEnabled: boolean;
  /** The cloud-owned gone-quiet alert. Opt-IN: default OFF. */
  readonly offlineAlertEnabled: boolean;
  /** How long the boat must be silent first, in minutes (15..10080). */
  readonly offlineThresholdMinutes: number;
  /**
   * READ-ONLY: whether the environment would ACTUALLY deliver an email (prod gate
   * AND kill switch). False in dev/staging — the card then shows the dry-run note.
   */
  readonly deliveryEnabled: boolean;
  /** READ-ONLY: the trailing-hour throttle's current state for this BOAT. */
  readonly rateLimit: RateLimitState;
}

/** A partial update — any field may be sent alone (the server merges). */
export interface NotificationPrefsPatch {
  readonly emailEnabled?: boolean;
  readonly minSeverity?: AlarmSeverity;
  readonly recoveryEmailEnabled?: boolean;
  readonly offlineAlertEnabled?: boolean;
  readonly offlineThresholdMinutes?: number;
}

export type NotificationPrefsState =
  | { kind: 'loading' }
  | { kind: 'ready'; prefs: NotificationPrefs }
  | { kind: 'error'; message: string };

/** The last save's outcome, for the small inline indicator beside the controls. */
export type SaveOutcome = 'idle' | 'saved' | 'error';

export interface UseNotificationPrefs {
  state: NotificationPrefsState;
  /** True while a PUT is in flight — the controls disable so no write races another. */
  saving: boolean;
  /** The last save's result: `saved` / `error` (drives the inline indicator). */
  saveOutcome: SaveOutcome;
  /** Human message when the last save failed (null otherwise). */
  saveError: string | null;
  /** PUT a partial update; the card reflects the RESPONSE (never optimistic). */
  save: (patch: NotificationPrefsPatch) => void;
  /** Re-run the GET — the error-state recovery path. */
  reload: () => void;
}

/**
 * Coerce a wire severity into a known {@link AlarmSeverity} — an unrecognized /
 * missing value falls back to the `warning` default so a malformed row never
 * strands the select on an empty value. Pure.
 */
export function coerceMinSeverity(value: unknown): AlarmSeverity {
  return typeof value === 'string' && (ALARM_SEVERITIES as readonly string[]).includes(value)
    ? (value as AlarmSeverity)
    : 'warning';
}

/**
 * Coerce a wire threshold into a usable minute count: the contract default (30)
 * for anything non-numeric, otherwise truncated and clamped into the server's
 * own 15..10080 range. Pure — never throws, never returns a value the server
 * would reject.
 */
export function coerceThresholdMinutes(value: unknown): number {
  const n = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isFinite(n)) return OFFLINE_THRESHOLD_DEFAULT_MINUTES;
  return Math.min(
    OFFLINE_THRESHOLD_MAX_MINUTES,
    Math.max(OFFLINE_THRESHOLD_MIN_MINUTES, Math.trunc(n))
  );
}

/** The rate-limit block when the server sent none — the honest "nothing known,
 *  nothing suppressed" reading, matching the server's own empty response. */
const NO_RATE_LIMIT: RateLimitState = {
  limited: false,
  scope: 'boat_alert',
  max: 0,
  windowMinutes: 60,
  since: null,
  resumesAt: null,
  alerts: [],
};

function parseRateLimitedAlert(raw: unknown): RateLimitedAlert | null {
  const o = (raw ?? {}) as Record<string, unknown>;
  if (typeof o.alertId !== 'string' || o.alertId === '') return null;
  return {
    alertId: o.alertId,
    sentInWindow: typeof o.sentInWindow === 'number' ? o.sentInWindow : 0,
    since: typeof o.since === 'string' ? o.since : '',
    resumesAt: typeof o.resumesAt === 'string' ? o.resumesAt : '',
  };
}

/**
 * Defensively parse the derived `rateLimit` block. Fail-CLOSED on the claim that
 * matters: `limited` is only true when the server said exactly `true`, so a
 * malformed body can never invent a "notifications paused" banner. Pure.
 */
export function parseRateLimit(raw: unknown): RateLimitState {
  if (raw === null || typeof raw !== 'object') return NO_RATE_LIMIT;
  const o = raw as Record<string, unknown>;
  const alerts = Array.isArray(o.alerts)
    ? o.alerts.map(parseRateLimitedAlert).filter((a): a is RateLimitedAlert => a !== null)
    : [];
  return {
    limited: o.limited === true,
    scope: typeof o.scope === 'string' ? o.scope : NO_RATE_LIMIT.scope,
    max: typeof o.max === 'number' ? o.max : NO_RATE_LIMIT.max,
    windowMinutes:
      typeof o.windowMinutes === 'number' ? o.windowMinutes : NO_RATE_LIMIT.windowMinutes,
    since: typeof o.since === 'string' ? o.since : null,
    resumesAt: typeof o.resumesAt === 'string' ? o.resumesAt : null,
    alerts,
  };
}

/**
 * Defensively parse a notification-prefs response into a typed
 * {@link NotificationPrefs}. Every absent field falls back to the CONTRACT
 * default — email on, `warning` floor, recovery mail on — except the two claims
 * that must fail closed: `deliveryEnabled` (never CLAIM delivery the server
 * didn't assert) and `offlineAlertEnabled` (never claim the owner opted in to a
 * gone-quiet alert they did not). Pure.
 */
export function parseNotificationPrefs(raw: unknown): NotificationPrefs {
  const obj = (raw ?? {}) as Record<string, unknown>;
  return {
    emailEnabled: typeof obj.emailEnabled === 'boolean' ? obj.emailEnabled : true,
    minSeverity: coerceMinSeverity(obj.minSeverity),
    recoveryEmailEnabled:
      typeof obj.recoveryEmailEnabled === 'boolean' ? obj.recoveryEmailEnabled : true,
    offlineAlertEnabled: obj.offlineAlertEnabled === true,
    offlineThresholdMinutes: coerceThresholdMinutes(obj.offlineThresholdMinutes),
    deliveryEnabled: obj.deliveryEnabled === true,
    rateLimit: parseRateLimit(obj.rateLimit),
  };
}

/**
 * The honest floor label for the severity select — an INCLUSIVE floor, so it
 * reads "and above" rather than pretending it matches only that one severity.
 * `caution` (the lowest) is "Everything". Pure.
 */
export function minSeverityFloorLabel(severity: AlarmSeverity): string {
  switch (severity) {
    case 'emergency':
      return 'Emergency only';
    case 'alarm':
      return 'Alarm and above';
    case 'warning':
      return 'Warning and above';
    case 'caution':
      return 'Everything';
    default:
      return 'Warning and above';
  }
}

/**
 * A gone-quiet threshold in the owner's words: "15 minutes of silence",
 * "1 hour of silence", "2 days of silence". Whole hours and whole days read as
 * such; anything else stays in minutes rather than rounding a real setting into
 * a lie. Pure.
 */
export function offlineThresholdLabel(minutes: number): string {
  const m = Math.max(1, Math.trunc(minutes));
  if (m % 1440 === 0) {
    const days = m / 1440;
    return `${days} ${days === 1 ? 'day' : 'days'} of silence`;
  }
  if (m % 60 === 0) {
    const hours = m / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} of silence`;
  }
  return `${m} minutes of silence`;
}

/**
 * The threshold values to offer, given what is currently stored. Normally the
 * three canonical choices; a stored value outside them (written straight to the
 * API — the server accepts anything in 15..10080) is INCLUDED and sorted in, so
 * the select shows the real setting instead of rendering blank or silently
 * snapping it to 30. Pure.
 */
export function offlineThresholdOptions(current: number): number[] {
  const stored = coerceThresholdMinutes(current);
  const set = new Set<number>([...OFFLINE_THRESHOLD_CHOICES, stored]);
  return [...set].sort((a, b) => a - b);
}

/**
 * `resumesAt` as a local clock time — "3:45 pm" — or null when the server sent
 * nothing parseable. Local, not UTC: the browser knows the reader's timezone
 * (the server's own emails do not, which is why they say UTC). Pure.
 */
export function rateLimitResumeClock(resumesAt: string | null): string | null {
  if (resumesAt === null || resumesAt === '') return null;
  const at = new Date(resumesAt);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "an hour" for the 60-minute window, else a plain minute count. */
function windowPhrase(windowMinutes: number): string {
  return windowMinutes === 60 ? 'an hour' : `${windowMinutes} minutes`;
}

/**
 * The detail line under the "notifications paused" banner. Says three true
 * things the silent throttle never did: how many alarms hit the cap, when email
 * resumes, and — the one a per-user settings card must not fudge — that the cap
 * is per ALARM and shared by everyone who gets mail about this boat, not a
 * personal quota (`scope: 'boat_alert'`, §5.9). Pure.
 */
export function rateLimitDetail(rateLimit: RateLimitState): string {
  const n = rateLimit.alerts.length;
  const cap =
    rateLimit.max > 0 ? ` of ${rateLimit.max} emails ${windowPhrase(rateLimit.windowMinutes)}` : '';
  const head =
    n === 0
      ? `Alarm emails for this boat have hit their limit${cap}.`
      : `${n} ${n === 1 ? 'alarm has' : 'alarms have'} hit the limit${cap}.`;
  const clock = rateLimitResumeClock(rateLimit.resumesAt);
  const resume = clock === null ? '' : ` Email resumes by ${clock}.`;
  return `${head}${resume} The limit is per alarm and shared by everyone who gets alarm emails for this boat — it is not a limit on you.`;
}

/**
 * Whether to render the "notifications paused" banner for THIS reader.
 *
 * `rateLimit` is boat-scoped: it says this boat's alarm mail is capped, which
 * stays true no matter who is looking. "Paused" is a claim about the reader,
 * and for an owner whose own master switch is off it is simply false — nothing
 * of theirs is paused, because nothing of theirs was going to be sent. Telling
 * them their notifications are paused invites them to wait for a resumption
 * that will never come, and hides the actual reason (their own setting) behind
 * a system-sounding one.
 *
 * So the banner needs BOTH: the boat is capped AND this user has alarm mail on.
 * The detail line underneath is unchanged — when it does show, it still says
 * the limit is per alarm and shared (§5.9). Pure.
 */
export function showRateLimitBanner(
  prefs: Pick<NotificationPrefs, 'emailEnabled' | 'rateLimit'>
): boolean {
  return prefs.emailEnabled && prefs.rateLimit.limited;
}

/** Map a fetch/PUT failure to an honest message (mirrors `useBoatAlerts`). */
export function notificationPrefsErrorMessage(err: unknown): string {
  const e = (err ?? {}) as { status?: number; message?: string };
  if (e.status === 403 || e.status === 404) return 'You do not have access to this boat.';
  if (e.status === 401) return 'Please sign in again.';
  return e.message ?? 'Failed to load your notification settings.';
}

export function useNotificationPrefs(boatId: string): UseNotificationPrefs {
  const [state, setState] = useState<NotificationPrefsState>({ kind: 'loading' });
  const [saving, setSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<SaveOutcome>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  // GET on mount / boatId change / reload. No WS — a personal setting is read
  // once and re-read only on explicit recovery.
  useEffect(() => {
    if (boatId === '') return;
    let cancelled = false;
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    void (async () => {
      try {
        const data = await api<unknown>(`/v1/boats/${boatId}/notification-prefs`);
        if (!cancelled) setState({ kind: 'ready', prefs: parseNotificationPrefs(data) });
      } catch (err) {
        if (!cancelled) setState({ kind: 'error', message: notificationPrefsErrorMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boatId, tick]);

  const inFlight = useRef(false);
  const save = useCallback(
    (patch: NotificationPrefsPatch) => {
      if (boatId === '' || inFlight.current) return;
      inFlight.current = true;
      setSaving(true);
      setSaveOutcome('idle');
      setSaveError(null);
      void (async () => {
        try {
          // The PUT returns the FULL, merged prefs — we reflect that verbatim
          // (no optimistic flip), so the card can never show a value the server
          // didn't store. `deliveryEnabled` is derived server-side and never sent.
          const data = await api<unknown>(`/v1/boats/${boatId}/notification-prefs`, {
            method: 'PUT',
            body: patch,
          });
          setState({ kind: 'ready', prefs: parseNotificationPrefs(data) });
          setSaveOutcome('saved');
        } catch (err) {
          setSaveOutcome('error');
          setSaveError(notificationPrefsErrorMessage(err));
        } finally {
          inFlight.current = false;
          setSaving(false);
        }
      })();
    },
    [boatId]
  );

  return { state, saving, saveOutcome, saveError, save, reload };
}
